import { evaluateSource } from '../../behaviors/src/lib/evaluateSource';
import { configBehaviorsFunc, ensureConfigBehaviorsReady } from '../nativeBehaviors/configBehaviors';

/**
 * Groups form IDs by their formTypeIdentifier
 * @param {Array<string>} ids - Array of form IDs
 * @param {Array<Object>} cabinetList - List of forms from the cabinet
 * @returns {Object} Object mapping formTypeIdentifier to arrays of IDs
 */
export const groupIdsByFormType = (ids, cabinetList) => {
  const idsByFormType = {};
  ids.forEach(id => {
    const form = cabinetList.find(f => f.id === id);
    if (form && form.formTypeIdentifier) {
      if (idsByFormType[form.formTypeIdentifier] === undefined) {
        idsByFormType[form.formTypeIdentifier] = [];
      }
      idsByFormType[form.formTypeIdentifier].push(id);
    }
  });
  return idsByFormType;
};

/**
 * Creates native functions for behavior execution
 * @param {Function} getNativeBehaviorEvaluator - Function to get the native behavior evaluator
 * @param {Function} errorHandler - Error handler function
 * @returns {Object} Native functions object
 */
export const createNativeFunctions = (getNativeBehaviorEvaluator, errorHandler) => {
  // The parameters to nativeBehaviorEvaluator.bind are:
  //   1. The context object (currently an empty object, can be used to pass contextual data)
  //   2. The resource type (currently hardcoded as 'form',
  //      indicating the evaluator operates on form objects)
  // If other resource types or context objects are needed in the future,
  // consider making these parameters configurable.
  const nativeBehaviorEvaluator = getNativeBehaviorEvaluator();
  const evaluator = nativeBehaviorEvaluator.bind(null, {}, 'form');
  return {
    errorHandler,
    evaluator,
    configBehaviors: configBehaviorsFunc
  };
};

/**
 * Executes behaviors sequentially for bulk operations
 * @param {Array<Object>} actions - Array of behavior actions to execute
 * @param {string} cabinetIdentifier - Cabinet identifier
 * @param {Object} idsByFormType - Object mapping formTypeIdentifier to arrays of IDs
 * @param {Object} filters - Filter object from indexQuery
 * @param {Object} nativeFunctions - Native functions { errorHandler, evaluator, configBehaviors }
 */
export const executeBehaviorsSequentially = async (actions, cabinetIdentifier, idsByFormType, filters, nativeFunctions) => {
  const transientContext = {
    cabinetIdentifier,
    selection: idsByFormType,
    filters: filters || {}
  };
  for (let i = 0; i < actions.length; i += 1) {
    const action = actions[i];
    // eslint-disable-next-line no-await-in-loop
    await evaluateSource(action, {}, '/', transientContext, nativeFunctions);
  }
};

/**
 * Main utility function to execute bulk behaviors
 * Groups IDs by form type, sets up native functions, and executes behaviors sequentially
 * @param {Object} params - Parameters object
 * @param {Array<string>} params.ids - Array of form IDs
 * @param {Array<Object>} params.cabinetList - List of forms from the cabinet
 * @param {Array<Object>} params.actions - Array of behavior actions to execute
 * @param {string} params.cabinetIdentifier - Cabinet identifier
 * @param {Object} params.filters - Filter object from indexQuery
 * @param {Function} params.getNativeBehaviorEvaluator - Function to get native behavior evaluator
 * @param {Function} params.errorHandler - Error handler function
 */
export const executeBulkBehaviors = async ({
  ids,
  cabinetList,
  actions,
  cabinetIdentifier,
  filters,
  getNativeBehaviorEvaluator,
  errorHandler
}) => {
  await ensureConfigBehaviorsReady();
  const idsByFormType = groupIdsByFormType(ids, cabinetList);
  const nativeFunctions = createNativeFunctions(getNativeBehaviorEvaluator, errorHandler);
  await executeBehaviorsSequentially(actions, cabinetIdentifier, idsByFormType, filters, nativeFunctions);
};
export default {
  groupIdsByFormType,
  createNativeFunctions,
  executeBehaviorsSequentially,
  executeBulkBehaviors
};